/**
 * WCAG 2.1 Contrast & Readability Calculation Utilities
 * Powers real-time accessibility guidance and contrast previews in Zirro Web Builder.
 */

export interface RgbColor {
  r: number;
  g: number;
  b: number;
  a?: number;
}

const COLOR_NAME_MAP: Record<string, string> = {
  black: "#000000",
  white: "#ffffff",
  slate: "#0f172a",
  primary: "#4f46e5",
  secondary: "#0ea5e9",
  accent: "#f59e0b",
  background: "#ffffff",
  surface: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
};

/**
 * Parses any hex, rgb(), rgba(), or color token string into RGB numbers [0..255].
 */
export function parseColor(colorStr?: string | null): RgbColor | null {
  if (!colorStr || typeof colorStr !== "string") return null;
  const s = colorStr.trim().toLowerCase();

  if (s === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  if (COLOR_NAME_MAP[s]) return parseColor(COLOR_NAME_MAP[s]);

  // CSS Var handling
  if (s.includes("var(--zirro-color-primary")) return parseColor("#4f46e5");
  if (s.includes("var(--zirro-color-secondary")) return parseColor("#0ea5e9");
  if (s.includes("var(--zirro-color-accent")) return parseColor("#f59e0b");
  if (s.includes("var(--zirro-color-background")) return parseColor("#ffffff");
  if (s.includes("var(--zirro-color-surface")) return parseColor("#f8fafc");
  if (s.includes("var(--zirro-color-text")) return parseColor("#0f172a");
  if (s.includes("var(--zirro-color-muted")) return parseColor("#64748b");
  if (s.includes("var(--zirro-color-border")) return parseColor("#e2e8f0");

  // Hex color (#fff or #ffffff or #ffffffff)
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: 1,
      };
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1,
      };
    }
    if (hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: parseInt(hex.slice(6, 8), 16) / 255,
      };
    }
  }

  // rgb(...) or rgba(...)
  const rgbMatch = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1,
    };
  }

  return null;
}

/**
 * Calculates standard WCAG 2.1 relative luminance for an RGB color.
 * L = 0.2126 * R + 0.7152 * G + 0.0722 * B (linearized channels)
 */
export function getLuminance(rgb: RgbColor): number {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates WCAG 2.1 contrast ratio between two colors.
 * Returns ratio from 1:1 to 21:1.
 */
export function getContrastRatio(foreground: RgbColor, background: RgbColor): number {
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  const ratio = (lighter + 0.05) / (darker + 0.05);
  return Math.round(ratio * 10) / 10;
}

/**
 * Blends a semi-transparent overlay color over an underlying base color.
 */
export function blendOverlay(base: RgbColor, overlay: RgbColor, opacityFraction: number): RgbColor {
  const alpha = Math.max(0, Math.min(1, opacityFraction));
  return {
    r: Math.round(overlay.r * alpha + base.r * (1 - alpha)),
    g: Math.round(overlay.g * alpha + base.g * (1 - alpha)),
    b: Math.round(overlay.b * alpha + base.b * (1 - alpha)),
    a: 1,
  };
}

export interface ContrastEvaluation {
  ratio: number;
  scoreText: string;
  isNormalTextPass: boolean;
  isLargeTextPass: boolean;
  rating: "excellent" | "good" | "poor";
  message: string;
  recommendation?: string;
  suggestedTextColor?: string;
}

/**
 * Evaluates contrast between active text and effective background,
 * returning WCAG status, badge classification, and contextual advice.
 */
export function evaluateContrast(
  textColorStr: string = "#ffffff",
  effectiveBg: RgbColor | string = { r: 15, g: 23, b: 42 },
): ContrastEvaluation {
  const textRgb = parseColor(textColorStr) ?? { r: 255, g: 255, b: 255 };
  const bgRgb = typeof effectiveBg === "string" ? (parseColor(effectiveBg) ?? { r: 15, g: 23, b: 42 }) : effectiveBg;
  const ratio = getContrastRatio(textRgb, bgRgb);
  const bgLuminance = getLuminance(bgRgb);

  const isNormalTextPass = ratio >= 4.5;
  const isLargeTextPass = ratio >= 3.0;

  if (ratio >= 7.0) {
    return {
      ratio,
      scoreText: `${ratio}:1`,
      isNormalTextPass: true,
      isLargeTextPass: true,
      rating: "excellent",
      message: "AAA Compliant — Outstanding legibility on all devices.",
    };
  }

  if (ratio >= 4.5) {
    return {
      ratio,
      scoreText: `${ratio}:1`,
      isNormalTextPass: true,
      isLargeTextPass: true,
      rating: "good",
      message: "AA Compliant — Standard readable contrast.",
    };
  }

  if (ratio >= 3.0) {
    return {
      ratio,
      scoreText: `${ratio}:1`,
      isNormalTextPass: false,
      isLargeTextPass: true,
      rating: "good",
      message: "Large Text Pass (3:1+) — Adequate for large titles, consider increasing body contrast.",
      recommendation: bgLuminance > 0.5 ? "Darken text to #0f172a or increase overlay." : "Increase overlay to 50%+ or lighten text to #ffffff.",
      suggestedTextColor: bgLuminance > 0.5 ? "#0f172a" : "#ffffff",
    };
  }

  // Poor contrast (< 3.0:1)
  const betterColor = bgLuminance > 0.4 ? "#0f172a" : "#ffffff";
  return {
    ratio,
    scoreText: `${ratio}:1`,
    isNormalTextPass: false,
    isLargeTextPass: false,
    rating: "poor",
    message: "Low Contrast Warning — Text may be difficult to read.",
    recommendation: bgLuminance > 0.4 ? "Switch text to dark (#0f172a) or use a darker overlay." : "Switch text to white (#ffffff) or increase overlay opacity to 45%+.",
    suggestedTextColor: betterColor,
  };
}
